// frontend/src/hooks/useSpiralVisualizer.js
import { useEffect, useRef } from "react";
import { creerLecteurContinu } from "../utils/continuationPlayback";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

export default function useSpiralVisualizer(props) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // --- Scene ---
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);

    const camera = new THREE.PerspectiveCamera(60, 900 / 600, 0.1, 1000);
    camera.position.set(0, 0, 60);

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setSize(900, 600, false);

    // --- Manipulation souris : orbite, rotation, zoom ---
    //
    // Contrôles de CAMÉRA uniquement. Ils déplacent le point de vue, jamais la
    // scène : aucune position d'objet n'est modifiée, donc `__oracleSnapshot`
    // renvoie exactement les mêmes valeurs quelle que soit l'orientation de la
    // vue, et `dyn_entropy` n'en dépend pas.
    //
    // C'est la différence de fond avec l'ancienne boucle du front : celle-ci
    // recalculait les positions et concurrençait donc le moteur backend. Une
    // caméra, elle, n'entre dans aucun observable.
    //
    // Même implémentation que `useCubesVisualizer` et `useIcosahedronVisualizer` ;
    // `spiral` était le seul visualiseur à n'en avoir jamais eu.
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enableZoom = true;
    controls.enablePan = true;

    // Lights
    const ambient = new THREE.AmbientLight(0xffffff, 0.6);
    const dir = new THREE.DirectionalLight(0xffffff, 1.0);
    dir.position.set(10, 15, 10);
    scene.add(ambient, dir);

    // Group spiral
    const spiralGroup = new THREE.Group();
    scene.add(spiralGroup);

    // --- Variables ---
    let velocities = [];
    let t = 0;

    // ═══════════════════════════════════════════════════════════════════════
    // LE BACKEND POSSÈDE LA FORME ET LE MOUVEMENT
    // docs/DOCTRINE_GEOMETRIE_CANONIQUE.md
    //
    // Ce hook cumulait trois non-conformités :
    //   1. il appelait `/initial` mais IGNORAIT les positions reçues — il n'en
    //      gardait que le nombre, pour créer autant de sphères ;
    //   2. il avait sa propre loi de mouvement (« moteur vortex / tornade »,
    //      `radius = 8 + sin(t+i)*2`), concurrente de l'intégrateur backend ;
    //   3. il fabriquait des vitesses `Math.random()` qui partaient ensuite
    //      dans le snapshot d'entropie, comme si elles étaient mesurées.
    //
    // Il rend désormais la géométrie canonique et rejoue les frames servies par
    // `/animate`, produites par le vrai intégrateur. Les vitesses sont dérivées
    // du déplacement réel entre deux frames.
    // ═══════════════════════════════════════════════════════════════════════
    let lecteur = null;     // lecture CONTINUE — voir docs/SPEC_CONTINUATION.md
    // `frames` et `frameDt` ont été retirés avec le rejeu local : le lecteur
    // partagé porte les segments et rend deux frames contiguës à la demande.
    // Les vitesses restent dérivées du `dt` RÉEL de rendu, comme avant.
    let playhead = 0;       // position de lecture, en secondes

    fetch("/api/geometry/spiral/initial")
      .then((res) => res.json())
      .then((data) => {
        if (!Array.isArray(data.positions)) return;

        data.positions.forEach((pos, idx) => {
          const geo = new THREE.SphereGeometry(0.25, 16, 16);
          const mat = new THREE.MeshPhongMaterial({
            color: new THREE.Color().setHSL(idx / data.positions.length, 1, 0.5),
          });
          const mesh = new THREE.Mesh(geo, mat);
          // Position CANONIQUE reçue du backend, plus une disposition inventée.
          mesh.position.set(pos[0], pos[1], pos[2]);
          spiralGroup.add(mesh);
          velocities.push(new THREE.Vector3(0, 0, 0));
        });

        // ⚠️ LES CURSEURS SONT TRANSMIS AU MOTEUR, pas appliqués au rejeu.
        //
        // Cette requête ne portait que `steps` et `dt`. Côté serveur,
        // `rotation_speed`, `oscillation_amplitude`, `vertical_wave_speed` et
        // `twist_factor` restaient donc à leurs valeurs par défaut : le
        // mouvement affiché était FIGÉ, identique quels que soient les
        // réglages. Mesuré, la cohérence directionnelle valait 0,654 — un
        // mouvement quasi translatif — au lieu de 0,168 une fois les commandes
        // rebranchées.
        //
        // ⚠️ LECTURE CONTINUE, plus un segment unique rebouclé : à la dernière
        // frame la scène se téléportait vers la première (39x le pas typique).
        lecteur = creerLecteurContinu({
          url: "/api/geometry/spiral/animate",
          parametres: () => ({
            steps: 240,
            dt: 0.05,
            spiral_speed: props.spiralSpeedPct / 100,
            chaos: props.chaos ? "true" : "false",
          }),
        });
        lecteur.demarrer();
        return null;
      })
      .catch((err) => console.error("Erreur chargement spirale:", err));

    // --- Snapshot for backend ---
    canvas.__oracleSnapshot = () => ({
      timestamp: Date.now() / 1000, // ✅ temps réel (secondes UNIX)
      positions: spiralGroup.children.map((s) => s.position.toArray()),
      colors: spiralGroup.children.map((s) => s.material.color.toArray()),
      velocities: velocities.map((v) => v.toArray()),
    });

    // --- Animate loop ---
    let prev = performance.now();
    function animate() {
      requestAnimationFrame(animate);
      const now = performance.now();
      const dt = Math.min((now - prev) / 1000, 0.05);
      prev = now;
      t += dt;

      if (props.running && lecteur) {
        // --- Lecture des frames backend, à VITESSE RÉELLE ---
        //
        // ⚠️ Le curseur pilotait ici la CADENCE DE LECTURE
        // (`playhead += dt * (spiralSpeedPct / 100) * 3`). C'est le défaut
        // déjà corrigé sur `cubes`, resté sur ce module : un curseur appliqué
        // au rejeu est vidé de son sens physique.
        //
        // Pire ici : les vitesses sont dérivées du rejeu quelques lignes plus
        // bas. Accélérer la lecture multipliait donc directement les vitesses
        // MESURÉES — le curseur agissait sur la métrique sans passer par la
        // scène, ce que la règle 2 interdit précisément.
        //
        // Il agit désormais sur le moteur, en paramètre de `/animate`.
        playhead += dt;
        const vue = lecteur.lire(playhead);
        if (!vue) return;
        if (vue.bascule || vue.gel) playhead = 0;
        const alpha = vue.alpha;
        const pa = vue.a.positions;
        const pb = vue.b.positions;

        spiralGroup.children.forEach((sphere, i) => {
          const a = pa[i];
          const b = pb[i];
          if (a && b) {
            const nx = a[0] + (b[0] - a[0]) * alpha;
            const ny = a[1] + (b[1] - a[1]) * alpha;
            const nz = a[2] + (b[2] - a[2]) * alpha;
            // Vitesse MESURÉE : déplacement réel sur le pas écoulé.
            if (dt > 0) {
              velocities[i].set(
                (nx - sphere.position.x) / dt,
                (ny - sphere.position.y) / dt,
                (nz - sphere.position.z) / dt
              );
            }
            sphere.position.set(nx, ny, nz);
          }

          // glow (shift de teinte)
          if (props.glow) {
            const hue = (Math.sin(t + i) + 1) / 2;
            sphere.material.color.setHSL(hue, 0.7, 0.5);
          }

          // flash (pulsation)
          if (props.flashRatePct > 0) {
            const flash = 0.5 + 0.5 * Math.sin(t * 10 * (props.flashRatePct / 100));
            sphere.material.emissive = new THREE.Color(0xff0000);
            sphere.material.emissiveIntensity = flash * (props.flashRatePct / 100) * 1.5;
          } else {
            sphere.material.emissiveIntensity = 0;
          }
        });
      }

      // Amortissement de l'orbite — hors du bloc `running` : la caméra doit
      // rester manipulable même animation arrêtée.
      controls.update();

      renderer.render(scene, camera);
    }
    animate();

    // Cleanup
    return () => {
      // Arrêter le lecteur AVANT de libérer le renderer : il porte une requête
      // de préchargement en vol, qui écrirait dans un état démonté.
      if (lecteur) lecteur.arreter();
      delete canvas.__oracleSnapshot;
      controls.dispose();
      renderer.dispose();
      spiralGroup.clear();
    };
    // ⚠️ `props.chaos` AJOUTÉ aux dépendances : il paramètre désormais le
    // moteur (amplitude d'oscillation de `/animate`). Sans lui, basculer le
    // bouton Chaos ne redemanderait aucune séquence et n'aurait aucun effet.
  }, [props.running, props.spiralSpeedPct, props.chaos, props.glow, props.flashRatePct]);

  return canvasRef;
}
